import { CssVarsProvider } from "@mui/joy";
import { CssBaseline } from "@mui/joy";
import * as Sentry from "@sentry/react-router";
import { useEffect } from "react";
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
} from "react-router";

import type { Route } from "./+types/root";
import { ErrorPage } from "./modules/error/components/ErrorPage";

import "./app.css";

export const links: Route.LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
  },
  {
    rel: "icon",
    href: "/icon.svg",
    type: "image/svg+xml",
  },
  {
    rel: "icon",
    href: "/favicon.ico",
    sizes: "48x48",
  },
  {
    rel: "apple-touch-icon",
    href: "/icon.svg",
  },
  {
    rel: "manifest",
    href: "/manifest.json",
  },
  {
    rel: "sitemap",
    type: "application/xml",
    href: "/sitemap.xml",
  },
];

export function clientLoader() {
  return {};
}

export default function App() {
  useEffect(() => {
    if (import.meta.env.PROD) {
      Sentry.init({
        dsn: "https://f32682220811fbdb44a1c9298216cfdf@o4509976727191553.ingest.de.sentry.io/4510044684222544",
        // Adds request headers and IP for users, for more info visit:
        // https://docs.sentry.io/platforms/javascript/guides/react-router/configuration/options/#sendDefaultPii
        sendDefaultPii: true,
        integrations: [
          Sentry.reactRouterTracingIntegration(),
          Sentry.replayIntegration(),
          Sentry.feedbackIntegration({
            // Additional SDK configuration goes in here, for example:
            colorScheme: "system",
          }),
        ],
        // Enable logs to be sent to Sentry
        enableLogs: true,
        tracesSampleRate: 1.0, //  Capture 100% of the transactions
        // Set `tracePropagationTargets` to declare which URL(s) should have trace propagation enabled
        tracePropagationTargets: [/^\//, /^https:\/\/brands-api\.makarov\.app/],
        // Capture Replay for 10% of all sessions,
        // plus 100% of sessions with an error
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
      });
    }
  }, []);

  return (
    <html lang="uk">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#1976d2" />
        <meta name="author" content="Brandly" />
        <meta name="application-name" content="Brandly" />
        <meta name="apple-mobile-web-app-title" content="Brandly" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="format-detection" content="telephone=no" />
        <meta name="mobile-web-app-capable" content="yes" />
        <Meta />
        <Links />
      </head>
      <body>
        <CssVarsProvider disableTransitionOnChange defaultMode={"system"}>
          <CssBaseline />
          <Outlet />
        </CssVarsProvider>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = "Щось пішло не так";
  let details: string | undefined;
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "Сторінку не знайдено" : "Помилка";
    details =
      error.status === 404
        ? "Запитувана сторінка не знайдена"
        : error.statusText || "Сталася неочікувана помилка";
  } else if (error && error instanceof Error) {
    details = error.message;

    if (import.meta.env.DEV) {
      stack = error.stack;
    }
  }

  // Send error to Sentry
  Sentry.captureException(error);

  return (
    <CssVarsProvider disableTransitionOnChange defaultMode={"system"}>
      <CssBaseline />
      <ErrorPage message={message} details={details} stack={stack} />
    </CssVarsProvider>
  );
}
